/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import { Card, Descriptions, Spin, Button, Form, Modal, message, Input } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { customerDetailsTransactionTable } from '../../constants/customerDetailsTransactionTable';
import { useParams } from 'react-router-dom';
import { readableDateTime, TABLE_PAGINATION } from '../../utilities/helper';
import { getApi, postApi } from '../../redux/rest/restSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import { GET_CUSTOMER, REFUND_CUSTOMER } from '../../constants/actionTypes';
import { useDispatch } from 'react-redux';
import Can from '../../components/Can';
import PageHeader, { useGoBack } from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import { PERMISSIONS } from '../../constants/permissions';

const OverviewScreen = () => {
    const [loading, setLoading] = useState(true); // Set loading to true initially
    const { uuid } = useParams();
    const dispatch = useDispatch();
    const goBack = useGoBack();
    const [customerDetails, setCustomerDetails] = useState(null); // Initialize customerDetails as null
    const calculateOffset = (current, pageSize) => (current - 1) * pageSize;
    const [form] = Form.useForm();

    // Inside your OverviewScreen component
    const [pagination, setPagination] = useState({
        current: 1, // Initial current page
        pageSize: 10, // Page size
    });



    const [showModal, setShowModal] = useState(false);

    const getCustomerDetails = () => {
        dispatch(getApi({
            endpoint: `${GET_CUSTOMER}/${uuid}`,
        }))
            .then(unwrapResult)
            .then((result) => {
                setLoading(false);
                setCustomerDetails(get(result, 'data'));
            })
            .catch(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (!uuid) return;
        getCustomerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    if (loading) {
        return (
            <div className='page-loading'>
                <Spin size='large' spinning={loading} />
            </div>
        );
    }

    if (!customerDetails) {
        return null;
    }

    const handleRefund = (values) => {
        let payload = {
            refund_amount: values.amount,
        };

        if (values.transactionId) {
            payload.transaction_id = values.transactionId;
        }
        dispatch(postApi({
            endpoint: `${REFUND_CUSTOMER}/${uuid}`,
            payload: payload
        }))
            .then(unwrapResult)
            .then((result) => {
                setLoading(false);
                if (get(result, 'success')) {
                    message.success(get(result, 'message'));
                    setShowModal(false);
                    form.resetFields();
                    getCustomerDetails();
                    message.success('Refund successful!');
                }
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const handleModalCancel = () => {
        setShowModal(false);
        form.resetFields();
    };


    return (
        <div className='page'>
            <PageHeader
                onBack={goBack}
                title={[get(customerDetails, 'first_name'), get(customerDetails, 'last_name')].filter(Boolean).join(' ') || '--'}
            />

            <Card bordered={false} className='section-card'>
                <Descriptions column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }} size='default' bordered>
                    <Descriptions.Item label='First Name'>
                        {get(customerDetails, 'first_name')}
                    </Descriptions.Item>
                    <Descriptions.Item label='Last Name'>
                        {get(customerDetails, 'last_name')}
                    </Descriptions.Item>
                    <Descriptions.Item label='Email Address'>
                        {get(customerDetails, 'email_address')}
                    </Descriptions.Item>
                    <Descriptions.Item label='Current Balance'>
                        {
                            get(customerDetails, 'current_balance') ? `$${get(customerDetails, 'current_balance')}` : '--'
                        }
                    </Descriptions.Item>
                    <Descriptions.Item label='Created At'>
                        {
                            get(customerDetails, 'created_at')
                                ? readableDateTime(get(customerDetails, 'created_at'), 'dd/mm/yyyy hh:mm')
                                : '--'
                        }
                    </Descriptions.Item>
                    <Descriptions.Item label='Updated At'>
                        {
                            get(customerDetails, 'updated_at')
                                ? readableDateTime(get(customerDetails, 'updated_at'), 'dd/mm/yyyy hh:mm')
                                : '--'
                        }
                    </Descriptions.Item>
                </Descriptions>
                <Can permissions={[PERMISSIONS.REFUND_ORDERS]}>
                    <div className='action-bar'>
                        <Button
                            type="primary"
                            className='default-primary-btn'
                            icon={<DollarOutlined />}
                            onClick={() => {
                                setShowModal(true);
                            }}
                        >
                            Refund Customer
                        </Button>
                    </div>
                </Can>
            </Card>
            <Modal
                title="Refund Customer"
                open={showModal}
                onCancel={() => setShowModal(false)}
                footer={null}
            >
                <Form
                    form={form}
                    onFinish={handleRefund}
                    layout="vertical"
                >
                    <Form.Item
                        label="Amount"
                        name="amount"
                        rules={[
                            { required: true, message: 'Please input the amount!' },
                            { validator: (_, value) => (parseInt(value) || parseFloat(value)) > 0 ? Promise.resolve() : Promise.reject('Please enter a valid number!') }
                        ]}
                    >
                        <Input placeholder="Enter amount" />
                    </Form.Item>
                    <Form.Item
                        label="Transaction ID"
                        name="transactionId"
                    >
                        <Input placeholder="Enter transaction ID" />
                    </Form.Item>
                    <div className='modal-actions'>
                        <Button
                            className='default-secondary-btn'
                            onClick={() => handleModalCancel()}
                        >
                            Cancel
                        </Button>
                        <Button
                            type='primary'
                            className='default-primary-btn'
                            htmlType='submit'
                        >
                            Refund
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Card bordered={false} className='section-card mt-20'>
                <DataTable
                    scrollX={600}
                    rowKey='id'
                    emptyProps={{ noun: 'transactions' }}
                    dataSource={get(customerDetails, 'transactions')}
                    columns={customerDetailsTransactionTable(calculateOffset(pagination.current, pagination.pageSize))}
                    pagination={{
                        ...TABLE_PAGINATION,
                        ...pagination,
                        onChange: (current, pageSize) => {
                            setPagination({ current, pageSize });
                        },
                    }}

                />
            </Card>
        </div>
    );
};

export default OverviewScreen;
